import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { TrendingUp, TrendingDown, Star, Briefcase, LogOut, Plus, Trash2, CalendarIcon, DollarSign, User, Bell, Mail, Settings } from 'lucide-react';
import { fetchApi, formatPrice, formatPercent, formatDate, getChangeColor, cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  
  const [watchlist, setWatchlist] = useState([]);
  const [portfolio, setPortfolio] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  
  // Alert settings state
  const [alertSettings, setAlertSettings] = useState({
    email_alerts_enabled: true,
    min_confidence_threshold: 70,
    alert_on_bullish: true,
    alert_on_bearish: true,
    min_predicted_change: 3.0,
    alert_frequency: 'daily',
    alert_scope: 'watchlist_only',
    max_alerts_per_email: 10
  });
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  
  // Add portfolio dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    ticker: '',
    purchase_date: new Date(),
    quantity: '',
    amount: '',
    currency: 'USD',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [watchlistData, portfolioData, alertsData] = await Promise.all([
        fetchApi('/watchlist'),
        fetchApi('/portfolio'),
        fetchApi('/alerts/settings').catch(() => null)
      ]);
      setWatchlist(watchlistData.watchlist || []);
      setPortfolio(portfolioData);
      if (alertsData?.settings) {
        setAlertSettings(alertsData.settings);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, authLoading, navigate, loadData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  const saveAlertSettings = async () => {
    setSavingAlerts(true);
    try {
      await fetchApi('/alerts/settings', {
        method: 'PUT',
        body: JSON.stringify({
          email_alerts_enabled: alertSettings.email_alerts_enabled,
          min_confidence_threshold: alertSettings.min_confidence_threshold,
          alert_on_bullish: alertSettings.alert_on_bullish,
          alert_on_bearish: alertSettings.alert_on_bearish,
          min_predicted_change: alertSettings.min_predicted_change,
          alert_frequency: alertSettings.alert_frequency,
          alert_scope: alertSettings.alert_scope,
          max_alerts_per_email: alertSettings.max_alerts_per_email
        })
      });
      toast.success('Alert settings saved!');
      setAlertSettingsOpen(false);
    } catch (error) {
      toast.error('Failed to save alert settings');
    } finally {
      setSavingAlerts(false);
    }
  };
  
  const sendTestAlert = async () => {
    try {
      await fetchApi('/alerts/test', { method: 'POST' });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.message || 'Failed to send test email');
    }
  };

  const removeFromWatchlist = async (ticker) => {
    try {
      await fetchApi(`/watchlist/${ticker}`, { method: 'DELETE' });
      setWatchlist(prev => prev.filter(item => item.ticker !== ticker));
      toast.success('Removed from watchlist');
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const addPortfolioEntry = async (e) => {
    e.preventDefault();
    try {
      await fetchApi('/portfolio', {
        method: 'POST',
        body: JSON.stringify({
          ticker: newEntry.ticker,
          purchase_date: newEntry.purchase_date.toISOString(),
          quantity: newEntry.quantity ? parseFloat(newEntry.quantity) : null,
          amount: newEntry.amount ? parseFloat(newEntry.amount) : null,
          currency: newEntry.currency,
          notes: newEntry.notes || null
        })
      });
      toast.success('Position added to portfolio');
      setAddDialogOpen(false);
      setNewEntry({
        ticker: '',
        purchase_date: new Date(),
        quantity: '',
        amount: '',
        currency: 'USD',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to add position');
    }
  };

  const deletePortfolioEntry = async (entryId) => {
    try {
      await fetchApi(`/portfolio/${entryId}`, { method: 'DELETE' });
      toast.success('Position removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove position');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">MarketMind</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/stocks" className="text-muted-foreground hover:text-foreground transition-colors">
              Stocks
            </Link>
            <Link to="/forum" className="text-muted-foreground hover:text-foreground transition-colors">
              Forum
            </Link>
            <Link to="/search" className="text-muted-foreground hover:text-foreground transition-colors">
              Advanced Search
            </Link>
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
              )}
              <span className="text-sm hidden sm:inline">{user?.name || user?.email}</span>
            </div>
            <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Trader'}
          </h1>
          <p className="text-muted-foreground">
            Track your portfolio and watchlist predictions
          </p>
        </div>

        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Invested</p>
              <p className="font-mono text-2xl font-bold">{formatPrice(portfolio.summary?.total_invested || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Current Value</p>
              <p className="font-mono text-2xl font-bold">{formatPrice(portfolio.summary?.total_current || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Gain/Loss</p>
              <p className={cn("font-mono text-2xl font-bold", getChangeColor(portfolio.summary?.total_gain || 0))}>
                {formatPrice(portfolio.summary?.total_gain || 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Return %</p>
              <p className={cn("font-mono text-2xl font-bold", getChangeColor(portfolio.summary?.total_gain_pct || 0))}>
                {formatPercent(portfolio.summary?.total_gain_pct || 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Watchlist */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  <CardTitle>Watchlist</CardTitle>
                </div>
                <Button data-testid="browse-stocks-btn" variant="outline" size="sm" onClick={() => navigate('/stocks')}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <CardDescription>{watchlist.length} stocks tracked</CardDescription>
            </CardHeader>
            <CardContent>
              {watchlist.length === 0 ? (
                <div className="py-8 text-center">
                  <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No stocks in your watchlist</p>
                  <Button onClick={() => navigate('/stocks')}>Browse Stocks</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {watchlist.map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between p-3 border border-border/50 rounded-sm hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/stocks/${item.ticker}`)}
                      data-testid={`watchlist-item-${item.ticker}`}
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-mono font-semibold">{item.ticker}</p>
                          <p className="text-sm text-muted-foreground">{item.stock?.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-mono">{formatPrice(item.stock?.current_price)}</p>
                          <p className={cn("text-sm font-mono", getChangeColor(item.stock?.price_change_pct_24h))}>
                            {formatPercent(item.stock?.price_change_pct_24h)}
                          </p>
                        </div>
                        {item.prediction && (
                          <Badge
                            variant="outline"
                            className={cn(
                              item.prediction.predicted_change_pct > 0 ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500'
                            )}
                          >
                            {formatPercent(item.prediction.predicted_change_pct)}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromWatchlist(item.ticker);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Portfolio */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <CardTitle>Portfolio</CardTitle>
                </div>
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-position-btn" variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Add Position
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Portfolio Position</DialogTitle>
                      <DialogDescription>Record a stock purchase to track its performance</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={addPortfolioEntry} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="ticker">Stock Ticker</Label>
                        <Input
                          id="ticker"
                          data-testid="portfolio-ticker-input"
                          placeholder="AAPL"
                          value={newEntry.ticker}
                          onChange={(e) => setNewEntry({ ...newEntry, ticker: e.target.value.toUpperCase() })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Purchase Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              data-testid="portfolio-date-btn"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {newEntry.purchase_date ? format(newEntry.purchase_date, 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={newEntry.purchase_date}
                              onSelect={(date) => setNewEntry({ ...newEntry, purchase_date: date || new Date() })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="quantity">Shares (optional)</Label>
                          <Input
                            id="quantity"
                            data-testid="portfolio-quantity-input"
                            type="number"
                            step="any"
                            placeholder="10"
                            value={newEntry.quantity}
                            onChange={(e) => setNewEntry({ ...newEntry, quantity: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="amount">Amount (optional)</Label>
                          <Input
                            id="amount"
                            data-testid="portfolio-amount-input"
                            type="number"
                            step="any"
                            placeholder="1000"
                            value={newEntry.amount}
                            onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select
                          value={newEntry.currency}
                          onValueChange={(value) => setNewEntry({ ...newEntry, currency: value })}
                        >
                          <SelectTrigger data-testid="portfolio-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                            <SelectItem value="GBP">GBP (£)</SelectItem>
                            <SelectItem value="JPY">JPY (¥)</SelectItem>
                            <SelectItem value="INR">INR (₹)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notes (optional)</Label>
                        <Input
                          id="notes"
                          data-testid="portfolio-notes-input"
                          placeholder="E.g., Long-term hold"
                          value={newEntry.notes}
                          onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                        />
                      </div>
                      <Button data-testid="portfolio-submit-btn" type="submit" className="w-full">
                        Add Position
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <CardDescription>{portfolio.entries?.length || 0} positions</CardDescription>
            </CardHeader>
            <CardContent>
              {portfolio.entries?.length === 0 ? (
                <div className="py-8 text-center">
                  <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No positions in your portfolio</p>
                  <Button onClick={() => setAddDialogOpen(true)}>Add Position</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {portfolio.entries.map((entry) => (
                    <div
                      key={entry.entry_id}
                      className="flex items-center justify-between p-3 border border-border/50 rounded-sm hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/stocks/${entry.ticker}`)}
                      data-testid={`portfolio-item-${entry.ticker}`}
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-mono font-semibold">{entry.ticker}</p>
                          <p className="text-sm text-muted-foreground">
                            {entry.quantity.toFixed(2)} shares @ {formatPrice(entry.purchase_price)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-mono">{formatPrice(entry.current_value)}</p>
                          <p className={cn("text-sm font-mono", getChangeColor(entry.gain_loss_pct))}>
                            {formatPercent(entry.gain_loss_pct)} ({formatPrice(entry.gain_loss)})
                          </p>
                        </div>
                        {entry.prediction && (
                          <Badge
                            variant="outline"
                            className={cn(
                              entry.prediction.predicted_change_pct > 0 ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500'
                            )}
                          >
                            AI: {formatPercent(entry.prediction.predicted_change_pct)}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePortfolioEntry(entry.entry_id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Alert Settings Section */}
        <Card className="mt-6 bg-card border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                <CardTitle>Alert Settings</CardTitle>
              </div>
              <Dialog open={alertSettingsOpen} onOpenChange={setAlertSettingsOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="alert-settings-btn" variant="outline" size="sm">
                    <Settings className="w-4 h-4 mr-1" /> Configure
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Alert Preferences</DialogTitle>
                    <DialogDescription>Configure when you want to receive prediction alerts</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    {/* Enable/Disable Alerts */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Email Alerts</Label>
                        <p className="text-sm text-muted-foreground">Receive alerts via email</p>
                      </div>
                      <Switch
                        data-testid="alerts-enabled-switch"
                        checked={alertSettings.email_alerts_enabled}
                        onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, email_alerts_enabled: checked })}
                      />
                    </div>
                    
                    {/* Confidence Threshold */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Minimum Confidence</Label>
                        <span className="text-sm font-mono text-primary">{alertSettings.min_confidence_threshold}%</span>
                      </div>
                      <Slider
                        data-testid="confidence-slider"
                        value={[alertSettings.min_confidence_threshold]}
                        onValueChange={([value]) => setAlertSettings({ ...alertSettings, min_confidence_threshold: value })}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">Only alert for predictions with confidence above this threshold</p>
                    </div>
                    
                    {/* Minimum Change */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Minimum Predicted Change</Label>
                        <span className="text-sm font-mono text-primary">{alertSettings.min_predicted_change}%</span>
                      </div>
                      <Slider
                        data-testid="change-slider"
                        value={[alertSettings.min_predicted_change]}
                        onValueChange={([value]) => setAlertSettings({ ...alertSettings, min_predicted_change: value })}
                        min={0}
                        max={20}
                        step={0.5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">Only alert when predicted change exceeds this %</p>
                    </div>
                    
                    {/* Direction Preferences */}
                    <div className="space-y-3">
                      <Label>Alert Direction</Label>
                      <div className="flex gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            data-testid="bullish-switch"
                            checked={alertSettings.alert_on_bullish}
                            onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, alert_on_bullish: checked })}
                          />
                          <span className="text-sm text-green-500">Bullish</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            data-testid="bearish-switch"
                            checked={alertSettings.alert_on_bearish}
                            onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, alert_on_bearish: checked })}
                          />
                          <span className="text-sm text-red-500">Bearish</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Frequency */}
                    <div className="space-y-2">
                      <Label>Alert Frequency</Label>
                      <Select
                        value={alertSettings.alert_frequency}
                        onValueChange={(value) => setAlertSettings({ ...alertSettings, alert_frequency: value })}
                      >
                        <SelectTrigger data-testid="frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediate</SelectItem>
                          <SelectItem value="daily">Daily Digest</SelectItem>
                          <SelectItem value="weekly">Weekly Summary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Alert Scope */}
                    <div className="space-y-2">
                      <Label>Alert Scope</Label>
                      <Select
                        value={alertSettings.alert_scope || 'watchlist_only'}
                        onValueChange={(value) => setAlertSettings({ ...alertSettings, alert_scope: value })}
                      >
                        <SelectTrigger data-testid="scope-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="watchlist_only">Watchlist Only</SelectItem>
                          <SelectItem value="all_stocks">All Tracked Stocks</SelectItem>
                          <SelectItem value="top_predictions">Top Predictions Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {alertSettings.alert_scope === 'watchlist_only' && 'Only receive alerts for stocks in your watchlist'}
                        {alertSettings.alert_scope === 'all_stocks' && 'Receive alerts for any stock matching your criteria'}
                        {alertSettings.alert_scope === 'top_predictions' && 'Receive alerts for the highest confidence predictions'}
                      </p>
                    </div>
                    
                    {/* Max Alerts (only show if not watchlist_only) */}
                    {alertSettings.alert_scope !== 'watchlist_only' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Max Stocks per Alert</Label>
                          <span className="text-sm font-mono text-primary">{alertSettings.max_alerts_per_email || 10}</span>
                        </div>
                        <Slider
                          data-testid="max-alerts-slider"
                          value={[alertSettings.max_alerts_per_email || 10]}
                          onValueChange={([value]) => setAlertSettings({ ...alertSettings, max_alerts_per_email: value })}
                          min={5}
                          max={25}
                          step={5}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">Limit the number of stocks included in each alert email</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-4">
                      <Button 
                        data-testid="save-alerts-btn"
                        className="flex-1" 
                        onClick={saveAlertSettings}
                        disabled={savingAlerts}
                      >
                        {savingAlerts ? 'Saving...' : 'Save Settings'}
                      </Button>
                      <Button 
                        data-testid="test-alert-btn"
                        variant="outline" 
                        onClick={sendTestAlert}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Test
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <CardDescription>Get notified about high-confidence predictions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", alertSettings.email_alerts_enabled ? "bg-green-500" : "bg-red-500")} />
                <span>{alertSettings.email_alerts_enabled ? 'Alerts Enabled' : 'Alerts Disabled'}</span>
              </div>
              <div className="text-muted-foreground">
                Scope: <span className="text-foreground capitalize">{(alertSettings.alert_scope || 'watchlist_only').replace(/_/g, ' ')}</span>
              </div>
              <div className="text-muted-foreground">
                Min Confidence: <span className="text-foreground font-mono">{alertSettings.min_confidence_threshold}%</span>
              </div>
              <div className="text-muted-foreground">
                Min Change: <span className="text-foreground font-mono">{alertSettings.min_predicted_change}%</span>
              </div>
              <div className="text-muted-foreground">
                Frequency: <span className="text-foreground capitalize">{alertSettings.alert_frequency}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
